import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getUserProfile } from '../api/users';
import { API_ORIGIN } from '../api/client';
import { CertifiedBadge } from '../components/auth/LoginButton';
import Spinner from '../components/common/Spinner';
import TileImage from '../components/tiles/TileImage';

export default function UserProfilePage() {
  const { username } = useParams<{ username: string }>();

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-profile', username],
    queryFn: () => getUserProfile(username!),
    enabled: !!username,
  });

  if (isLoading) {
    return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  }

  if (error || !data) {
    return (
      <div className="text-center py-20 text-red-600">
        ユーザーが見つかりません
      </div>
    );
  }

  const { user, hands } = data;

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-6">
      {/* Profile header */}
      <div className="flex items-center gap-4 p-4 border rounded-xl bg-white">
        {user.x_avatar_url ? (
          <img src={user.x_avatar_url} alt="" className="w-16 h-16 rounded-full" />
        ) : (
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center text-2xl font-bold text-gray-500">
            {user.x_display_name[0]}
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold">{user.x_display_name}</span>
            {user.is_pro && <CertifiedBadge />}
          </div>
          <span className="text-sm text-gray-500">@{user.x_username}</span>
        </div>
        <a
          href={`https://x.com/${user.x_username}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 bg-black text-white text-xs font-medium rounded-lg hover:bg-gray-800 transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
          </svg>
          Xで見る
        </a>
      </div>

      {/* Posts */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">
          投稿した局面
          <span className="ml-2 text-gray-400 font-normal">{hands.length}件</span>
        </h2>
        {hands.length === 0 ? (
          <div className="text-center text-gray-400 py-8 border rounded-xl text-sm">
            まだ局面を投稿していません
          </div>
        ) : (
          <div className="space-y-2">
            {hands.map((hand) => {
              const imageUrl = hand.source_image_url
                ? hand.source_image_url.startsWith('/')
                  ? `${API_ORIGIN}${hand.source_image_url}`
                  : hand.source_image_url
                : null;
              return (
                <Link
                  key={hand.id}
                  to={`/hands/${hand.id}`}
                  className="flex items-center gap-3 p-3 border rounded-xl bg-white hover:bg-gray-50 transition-colors"
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt=""
                      className="w-14 h-10 object-cover rounded border shrink-0"
                    />
                  ) : (
                    <div className="w-14 h-10 bg-gray-100 rounded border shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate text-sm">{hand.title ?? '無題の局面'}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {new Date(hand.created_at).toLocaleDateString('ja-JP')}
                      {' · '}💬 {hand.vote_count}票
                    </div>
                  </div>
                  {hand.mortal_best_tile && <TileImage tile={hand.mortal_best_tile} size="sm" />}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
